// ActivityLogReport.js — BUSINESS Activity Log viewer.
// Reads BusinessActivityLogs via POST /api/reports/business-activity — owner-
// facing hotel events only (bookings, check-ins, payments, invoices, restaurant
// bills…). No API URLs / HTTP methods / IP / developer entities. The technical
// ModuleAuditLog audit trail still exists internally for debugging.
import React, { useEffect, useState } from 'react';
import {
  Box, Container, Card, CardContent, Typography, TextField, Button,
  Grid, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TablePagination, Chip, Snackbar, Alert, CircularProgress, MenuItem,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';
import * as XLSX from 'xlsx';
import config from '../../config';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const currency = company?.currency ?? '';

const todayStr = () => new Date().toISOString().slice(0, 10);
const monthAgo = () =>
  new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

const MODULES = ['', 'Booking', 'Guest', 'Payment', 'Restaurant',
  'Housekeeping', 'Inventory', 'Hotel Master', 'User'];

const statusColor = (s) =>
  s === 'Cancelled' || s === 'Failed' ? 'error'
    : s === 'Pending' ? 'warning'
      : 'success';

const moduleColor = (m) =>
  m === 'Booking' ? 'primary'
    : m === 'Payment' ? 'success'
      : m === 'Restaurant' ? 'warning'
        : m === 'Inventory' ? 'secondary'
          : 'default';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '');
const fmtTime = (d) =>
  d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';
const refOf = (r) => r.bookingNo || r.invoiceNo || (r.referenceId ? `#${r.referenceId}` : '');
const guestOf = (r) => r.guestName || '—';

export default function ActivityLogReport() {
  const [filters, setFilters] = useState({
    fromDate: monthAgo(),
    toDate: todayStr(),
    module: '',
    search: '',
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ total: 0, rows: [] });
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const notify = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  const load = async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const body = {
        companyId: Number(company.id) || 0,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        module: filters.module || null,
        search: filters.search || null,
        page,
        pageSize: rowsPerPage,
      };
      const res = await fetch(`${config.API_BASE_URL}/reports/business-activity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setData({ total: json.total || 0, rows: json.rows || [] });
    } catch (e) {
      console.error(e);
      notify('Failed to load activity log', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, rowsPerPage]);

  const exportExcel = () => {
    const head = [[
      'Date', 'Time', 'Module', 'Activity', 'Reference No',
      'Guest / Customer', 'Amount', 'Performed By', 'Remarks', 'Status',
    ]];
    (data.rows || []).forEach((r) =>
      head.push([
        fmtDate(r.activityTime), fmtTime(r.activityTime), r.module, r.title,
        refOf(r), guestOf(r),
        r.amount != null ? `${currency} ${Number(r.amount).toFixed(2)}` : '',
        r.performedBy || '', r.remarks || r.description || '', r.status || '',
      ]),
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(head), 'Activity Log');
    XLSX.writeFile(wb, `ActivityLog_${filters.fromDate}_to_${filters.toDate}.xlsx`);
  };

  return (
    <Container maxWidth='xl' sx={{ py: 3, px: { xs: 1.5, sm: 3 } }}>
      <Box display='flex' alignItems='center' justifyContent='space-between' mb={2} flexWrap='wrap' gap={1}>
        <Box display='flex' alignItems='center' gap={1}>
          <HistoryIcon color='primary' />
          <Typography variant='h5' fontWeight='700'>Activity Log</Typography>
        </Box>
        <Button variant='outlined' startIcon={<DownloadIcon />} onClick={exportExcel} disabled={!data.rows?.length}>
          Excel
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3} md={2}>
              <TextField fullWidth size='small' type='date' label='From'
                InputLabelProps={{ shrink: true }} value={filters.fromDate}
                onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField fullWidth size='small' type='date' label='To'
                InputLabelProps={{ shrink: true }} value={filters.toDate}
                onChange={(e) => setFilters({ ...filters, toDate: e.target.value })} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField select fullWidth size='small' label='Module' value={filters.module}
                onChange={(e) => setFilters({ ...filters, module: e.target.value })}>
                {MODULES.map((m) => <MenuItem key={m || 'all'} value={m}>{m || 'All Modules'}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={9} md={4}>
              <TextField fullWidth size='small' label='Search (guest / booking / bill)' value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && (setPage(0), load())} />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <Button fullWidth variant='contained' startIcon={<RefreshIcon />}
                onClick={() => { setPage(0); load(); }} disabled={loading}>
                Apply
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Result table — business columns only */}
      <TableContainer component={Card} sx={{ overflowX: 'auto', width: '100%' }}>
        <Table size='small' stickyHeader sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow sx={{ '& th': { bgcolor: '#f5f5f5', fontWeight: 700 } }}>
              <TableCell>Date</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Module</TableCell>
              <TableCell>Activity</TableCell>
              <TableCell>Reference No</TableCell>
              <TableCell>Guest / Customer</TableCell>
              <TableCell align='right'>Amount</TableCell>
              <TableCell>Performed By</TableCell>
              <TableCell>Remarks</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={10} align='center'><CircularProgress size={24} /></TableCell></TableRow>
            )}
            {!loading && (data.rows || []).length === 0 && (
              <TableRow><TableCell colSpan={10} align='center'>No activity yet.</TableCell></TableRow>
            )}
            {!loading && (data.rows || []).map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{fmtDate(r.activityTime)}</TableCell>
                <TableCell>{fmtTime(r.activityTime)}</TableCell>
                <TableCell><Chip size='small' label={r.module} color={moduleColor(r.module)} /></TableCell>
                <TableCell>{r.title}</TableCell>
                <TableCell>{refOf(r)}</TableCell>
                <TableCell>{guestOf(r)}</TableCell>
                <TableCell align='right'>
                  {r.amount != null ? `${currency} ${Number(r.amount).toLocaleString('en-IN')}` : '—'}
                </TableCell>
                <TableCell>{r.performedBy || '—'}</TableCell>
                <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.remarks || r.description || '—'}
                </TableCell>
                <TableCell><Chip size='small' label={r.status || 'Success'} color={statusColor(r.status)} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component='div'
          count={data.total || 0}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]} />
      </TableContainer>

      <Snackbar open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snack.sev}>{snack.msg}</Alert>
      </Snackbar>
    </Container>
  );
}
